import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Building2 } from "@/lib/public-icons";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { PortalNotice } from "@/components/app/patterns/portal-notice";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/lib/host/auth-client";
import { orpc } from "@/lib/orpc";
import { PartnerShell } from "./-components/partner-shell";
import { PasswordGate } from "./-components/password-gate";
import { ResidentCard } from "./-components/resident-card";
import { ALL, matchesFilter, optionsOf } from "./-components/resident-filter";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/partner-portal/")({
  component: PartnerPortalPage,
});

/** Every resident this agency currently holds, across whichever communities they are in. */
function PartnerPortalPage() {
  const session = useSession();
  const enabled = Boolean(session.data);
  const [search, setSearch] = useState("");
  const [facility, setFacility] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);

  const me = useQuery({ ...orpc.partnerPortal.me.queryOptions({}), retry: false, enabled });
  const residents = useQuery({
    ...orpc.partnerPortal.residents.queryOptions({}),
    retry: false,
    enabled,
  });

  if (!session.data) {
    return (
      <PartnerShell>
        <PortalNotice
          icon={<Building2 />}
          title="Sign in to the partner portal"
          action={
            <Button asChild size="lg">
              <Link to="/login" search={{ next: "/partner-portal" }}>
                Sign in
              </Link>
            </Button>
          }
        >
          Use the login the community set up for your company.
        </PortalNotice>
      </PartnerShell>
    );
  }

  // Nothing is drawn until this answers. It carries both the company's name and whether the
  // password is still the temporary one, and a list of residents that flashed up before the gate
  // would be a chart opened with a password somebody at the desk still knows.
  if (me.isPending) {
    return (
      <PartnerShell name={session.data.user.name}>
        <Skeleton className="h-32 w-full" />
      </PartnerShell>
    );
  }

  if (me.isError) {
    return (
      <PartnerShell name={session.data.user.name}>
        {/* The API's own sentence, because each refusal names a different fix:
            the portal is switched off, the login is deactivated, or this is not
            a partner account at all. */}
        <PortalNotice icon={<Building2 />} tone="idle" title="This portal is not open to you">
          {errorWords(me.error)}
        </PortalNotice>
      </PartnerShell>
    );
  }

  if (me.data.user.mustResetPassword) {
    return (
      <PartnerShell institution={me.data.institution.name} name={me.data.user.name}>
        <PasswordGate />
      </PartnerShell>
    );
  }

  const rows = residents.data ?? [];
  const communities = optionsOf(rows, (row) => row.facilityName);
  const statuses = optionsOf(rows, (row) => row.status);
  const shown = rows.filter((row) => matchesFilter(row, { search, facility, status }));

  return (
    <PartnerShell institution={me.data.institution.name} name={me.data.user.name}>
      <div className="mb-6">
        <PageHeader title="Your residents" />
      </div>

      {rows.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchField
            label="Search residents"
            placeholder="Search name, room, community"
            value={search}
            onChange={setSearch}
          />
          {/* Offered only when there is something to choose between: an agency
              following one building has no use for a picker with one entry. */}
          {communities.length > 1 ? (
            <Select value={facility} onValueChange={setFacility}>
              <SelectTrigger aria-label="Filter by community" className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All communities</SelectItem>
                {communities.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {statuses.length > 1 ? (
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger aria-label="Filter by status" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {statuses.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      ) : null}

      {residents.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : residents.isError ? (
        <ReadFailed
          message="Could not load your residents."
          error={residents.error}
          onRetry={() => void residents.refetch()}
          retrying={residents.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No residents linked"
          description="The community has not linked any residents to your company yet."
        />
      ) : shown.length === 0 ? (
        <EmptyState
          title="Nobody matches"
          description="Clear the search or the filters to see everyone again."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {shown.map((row) => (
            <Link
              key={row.id}
              className="group focus-visible:ring-ring/80 rounded-xl outline-hidden focus-visible:ring-[3px]"
              params={{ residentId: row.id }}
              to="/partner-portal/$residentId"
            >
              <ResidentCard row={row} />
            </Link>
          ))}
        </div>
      )}
    </PartnerShell>
  );
}
